/**
 * Order PDF generators (per-page header + full-document body).
 * Split out from orderEmails.js to keep each file under the 500-line cap;
 * the produced HTML must remain identical to the previous monolithic version.
 */

/**
 * Generate Order PDF page header HTML (rendered separately on each PDF page).
 */
export function generatePdfHeaderHtml(pageNum, totalPages, dateStr, orderNumber = null) {
  const formattedOrderNumber = orderNumber ? String(orderNumber).padStart(7, '0') : null
  return `
<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="UTF-8">
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Rubik', Arial, sans-serif;
      direction: rtl;
      background: #fff;
      color: #1e293b;
      margin: 0;
      padding: 0 10px;
    }
  </style>
</head>
<body>
  <div style="text-align: center; border-bottom: 2px solid #283548; padding-bottom: 8px;">
    <h1 style="font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 2px;">GoFood</h1>
    <h2 style="font-size: 14px; font-weight: 600; color: #283548; margin-bottom: 4px;">אישור הזמנה${formattedOrderNumber ? ` #${formattedOrderNumber}` : ''}</h2>
    <p style="font-size: 10px; color: #64748b; margin-bottom: 2px;">תאריך: ${dateStr}</p>
    <p style="font-size: 10px; color: #64748b; font-weight: 500;">עמוד ${pageNum} מתוך ${totalPages}</p>
  </div>
</body>
</html>`
}

/**
 * Generate Order PDF HTML - Standalone document for PDF generation.
 * Same data shape as generateNewOrderEmailHtml but formatted for print/PDF.
 * @param {Object} orderData - Order data
 * @returns {string} Complete HTML document for PDF
 */
export function generateOrderPdfHtml(orderData) {
  const { client, items, totals, deposit, orderNumber, pointsToAward, payment, isPaid } = orderData
  const totalItemCount = items.reduce((sum, item) => sum + item.qty, 0)
  const formattedOrderNumber = orderNumber ? String(orderNumber).padStart(7, '0') : null

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00'
    return price.toFixed(2)
  }

  const formatDate = () => {
    return new Date().toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const maskId = (id) => {
    if (!id) return '—'
    const str = String(id)
    if (str.length <= 4) return str
    return '*'.repeat(str.length - 4) + str.slice(-4)
  }

  const formatPaymentDate = (timestamp) => {
    if (!timestamp) return '—'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Build items HTML for PDF (without images for cleaner print)
  const itemsHtml = items
    .map(
      (item, index) => `
    <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f8fafc'};">
      <td style="padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; font-size: 12px;">
        ${item.makat}
      </td>
      <td style="padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; font-size: 12px; color: #475569;">
        ${item.barcode || '—'}
      </td>
      <td style="padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: right;">
        <div style="font-weight: 500; color: #1e293b; font-size: 12px;">${item.name}</div>
        ${item.isPromoLine ? `<div style="font-size: 10px; color: #10b981;">${item.promo?.label || 'מבצע'}</div>` : ''}
        ${item.isDiscountLine ? `<div style="font-size: 10px; color: #283548;">הנחה ${item.discount?.discountPercent}%</div>` : ''}
      </td>
      <td style="padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: center; color: #475569; font-size: 12px;">
        ${item.qty}
      </td>
      <td style="padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 12px;">
        ${item.isPromoLine || item.isDiscountLine ? `<div style="font-size: 10px; color: #94a3b8; text-decoration: line-through;">${formatPrice(item.catalogPrice)} ₪</div>` : ''}
        <div style="color: #475569;">${formatPrice(item.unitPricePreVatPreDeposit)} ₪</div>
      </td>
      <td style="padding: 8px 10px; border-bottom: 1px solid #e2e8f0; text-align: left; font-weight: 600; color: #1e293b; font-size: 12px;">
        ${formatPrice(item.unitPricePreVatPreDeposit * item.qty)} ₪
      </td>
    </tr>
  `
    )
    .join('')

  const pdfTitle = formattedOrderNumber
    ? `אישור הזמנה #${formattedOrderNumber} - ${client.name || 'לקוח'}`
    : `אישור הזמנה - ${client.name || 'לקוח'}`

  return `
<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pdfTitle}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Rubik', Arial, sans-serif;
      direction: rtl;
      background: #fff;
      color: #1e293b;
      padding: 10px 15px;
      padding-top: 0;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
    }
    table { border-collapse: collapse; width: 100%; }
    /* Prevent page breaks inside rows only */
    tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }
  </style>
</head>
<body>
  <div style="max-width: 100%; margin: 0;">
    <!-- Header placeholder - actual header added per page by PDF generator -->
    <div id="header-space" style="height: 10px;"></div>

    <!-- Client Details -->
    <table width="100%" style="margin-bottom: 20px;">
      <tr>
        <td style="vertical-align: top; width: 50%;">
          <div style="font-weight: 600; color: #1e293b; font-size: 13px; margin-bottom: 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">פרטי לקוח</div>
          <div style="font-size: 12px; color: #475569; line-height: 1.6;">
            <div><strong>שם:</strong> ${client.name || '—'}</div>
            ${client.makat ? `<div><strong>מס׳ לקוח:</strong> ${client.makat}</div>` : ''}
            ${client.address ? `<div><strong>כתובת:</strong> ${client.address}</div>` : ''}
          </div>
        </td>
        <td style="vertical-align: top; width: 50%; padding-right: 20px;">
          <div style="font-weight: 600; color: #1e293b; font-size: 13px; margin-bottom: 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">פרטי הזמנה</div>
          <div style="font-size: 12px; color: #475569; line-height: 1.6;">
            ${formattedOrderNumber ? `<div><strong>מספר הזמנה:</strong> ${formattedOrderNumber}</div>` : ''}
            ${isPaid && payment?.paidAt ? `<div><strong>תאריך תשלום:</strong> ${formatPaymentDate(payment.paidAt)}</div>` : ''}
            <div><strong>הודפס בתאריך:</strong> ${formatDate()}</div>
            ${client.phone ? `<div><strong>טלפון:</strong> ${client.phone}</div>` : ''}
            ${client.email ? `<div><strong>אימייל:</strong> ${client.email}</div>` : ''}
          </div>
        </td>
      </tr>
    </table>

    <!-- Items Table -->
    <div style="font-weight: 600; color: #1e293b; font-size: 13px; margin-bottom: 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">פריטים בהזמנה</div>
    <table width="100%" style="border: 1px solid #e2e8f0; margin-bottom: 20px;">
      <tr style="background: #f8fafc;">
        <th style="padding: 8px 10px; text-align: right; font-weight: 600; color: #475569; font-size: 11px; border-bottom: 1px solid #e2e8f0; width: 70px;">מק"ט</th>
        <th style="padding: 8px 10px; text-align: right; font-weight: 600; color: #475569; font-size: 11px; border-bottom: 1px solid #e2e8f0; width: 90px;">ברקוד</th>
        <th style="padding: 8px 10px; text-align: right; font-weight: 600; color: #475569; font-size: 11px; border-bottom: 1px solid #e2e8f0;">תיאור פריט</th>
        <th style="padding: 8px 10px; text-align: center; font-weight: 600; color: #475569; font-size: 11px; border-bottom: 1px solid #e2e8f0; width: 50px;">כמות</th>
        <th style="padding: 8px 10px; text-align: left; font-weight: 600; color: #475569; font-size: 11px; border-bottom: 1px solid #e2e8f0; width: 70px;">מחיר יח׳</th>
        <th style="padding: 8px 10px; text-align: left; font-weight: 600; color: #475569; font-size: 11px; border-bottom: 1px solid #e2e8f0; width: 80px;">סה"כ</th>
      </tr>
      ${itemsHtml}
    </table>

    <!-- Pricing Summary -->
    <table width="100%" style="border: 1px solid #e2e8f0; margin-bottom: 20px;">
      ${
        totals.promoSavingsPreVat > 0
          ? `
      <tr>
        <td style="padding: 6px 10px; text-align: right; font-size: 12px; color: #10b981; border-bottom: 1px solid #f1f5f9;">חיסכון במבצעים</td>
        <td style="padding: 6px 10px; text-align: left; font-size: 12px; color: #10b981; font-weight: 500; border-bottom: 1px solid #f1f5f9; width: 100px;">-${formatPrice(totals.promoSavingsPreVat)} ₪</td>
      </tr>
      `
          : ''
      }
      ${
        totals.discountSavingsPreVat > 0
          ? `
      <tr>
        <td style="padding: 6px 10px; text-align: right; font-size: 12px; color: #283548; border-bottom: 1px solid #f1f5f9;">הנחות כמות</td>
        <td style="padding: 6px 10px; text-align: left; font-size: 12px; color: #283548; font-weight: 500; border-bottom: 1px solid #f1f5f9; width: 100px;">-${formatPrice(totals.discountSavingsPreVat)} ₪</td>
      </tr>
      `
          : ''
      }
      <tr>
        <td style="padding: 6px 10px; text-align: right; font-size: 12px; color: #475569; border-bottom: 1px solid #f1f5f9;">סה"כ פריטים</td>
        <td style="padding: 6px 10px; text-align: left; font-size: 12px; color: #475569; border-bottom: 1px solid #f1f5f9; width: 100px;">${totalItemCount} יח'</td>
      </tr>
      ${
        deposit.hasDeposit
          ? `
      <tr>
        <td style="padding: 6px 10px; text-align: right; font-size: 12px; color: #475569; border-bottom: 1px solid #f1f5f9;">פיקדון (${deposit.depositUnits} יח׳)</td>
        <td style="padding: 6px 10px; text-align: left; font-size: 12px; color: #475569; border-bottom: 1px solid #f1f5f9; width: 100px;">${formatPrice(deposit.depositPreVat)} ₪</td>
      </tr>
      `
          : ''
      }
      <tr>
        <td style="padding: 6px 10px; text-align: right; font-size: 12px; color: #475569; border-bottom: 1px solid #f1f5f9;">סה"כ לפני מע"מ</td>
        <td style="padding: 6px 10px; text-align: left; font-size: 12px; color: #475569; border-bottom: 1px solid #f1f5f9; width: 100px;">${formatPrice(totals.totalPreVat)} ₪</td>
      </tr>
      <tr>
        <td style="padding: 6px 10px; text-align: right; font-size: 12px; color: #64748b; border-bottom: 1px solid #f1f5f9;">מע"מ (18%)</td>
        <td style="padding: 6px 10px; text-align: left; font-size: 12px; color: #64748b; border-bottom: 1px solid #f1f5f9; width: 100px;">${formatPrice(totals.vatAmount)} ₪</td>
      </tr>
      <tr style="background: #f8fafc;">
        <td style="padding: 10px; text-align: right; font-size: 14px; font-weight: 700; color: #1e293b;">סה"כ לתשלום</td>
        <td style="padding: 10px; text-align: left; font-size: 14px; font-weight: 700; color: #283548; width: 100px;">${formatPrice(totals.totalWithVat)} ₪</td>
      </tr>
      ${
        pointsToAward > 0
          ? `
      <tr style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);">
        <td style="padding: 8px 10px; text-align: right; font-size: 12px; font-weight: 600; color: #b45309;">⭐ נקודות שתזכה לקבל</td>
        <td style="padding: 8px 10px; text-align: left; font-size: 12px; font-weight: 700; color: #b45309; width: 100px;">${pointsToAward} נקודות</td>
      </tr>
      `
          : ''
      }
    </table>

    ${
      isPaid && payment
        ? `
    <!-- Payment Details -->
    <div style="font-weight: 600; color: #1e293b; font-size: 13px; margin-bottom: 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">פרטי תשלום</div>
    <table width="100%" style="border: 1px solid #d1fae5; margin-bottom: 20px; background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border-radius: 8px;">
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0; width: 40%;">סכום ששולם</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 13px; color: #166534; font-weight: 600; border-bottom: 1px solid #bbf7d0;">${formatPrice(payment.amount)} ₪</td>
      </tr>
      ${
        payment.cardSuffix
          ? `
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">4 ספרות אחרונות</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0; direction: ltr;">**** ${payment.cardSuffix}</td>
      </tr>
      `
          : ''
      }
      ${
        payment.approvalNumber
          ? `
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">מספר אישור</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">${payment.approvalNumber}</td>
      </tr>
      `
          : ''
      }
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">בעל הכרטיס</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">${payment.holderName || '—'}</td>
      </tr>
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">ת"ז בעל הכרטיס</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0; direction: ltr;">${maskId(payment.identificationNumber)}</td>
      </tr>
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">מספר תשלומים</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">${payment.numberOfPayments || 1}</td>
      </tr>
      ${
        (payment.numberOfPayments || 1) > 1 && payment.firstPaymentAmount
          ? `
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">תשלום ראשון</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">${formatPrice(payment.firstPaymentAmount)} ₪</td>
      </tr>
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">תשלומים נוספים</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534; border-bottom: 1px solid #bbf7d0;">${formatPrice(payment.restPaymentsAmount || 0)} ₪</td>
      </tr>
      `
          : ''
      }
      ${
        payment.paidAt
          ? `
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #166534;">תאריך תשלום</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 12px; color: #166534;">${formatPaymentDate(payment.paidAt)}</td>
      </tr>
      `
          : ''
      }
    </table>
    `
        : ''
    }

    <!-- Footer -->
    <div style="border-top: 1px solid #e2e8f0; padding-top: 16px; text-align: center;">
      <p style="color: #64748b; font-size: 11px;">תודה על הזמנתך!</p>
      <p style="color: #94a3b8; font-size: 10px; margin-top: 4px;">© ${new Date().getFullYear()} GoFood. כל הזכויות שמורות.</p>
    </div>
  </div>
</body>
</html>
  `.trim()
}
